import { useRef, type ComponentType } from 'react'

export interface DashboardCategory {
  key: string
  label: string
  /** Optional leading icon — used by the Back item in the child menu. */
  icon?: ComponentType<{ className?: string }>
  /** Tooltip shown when the category is disabled. */
  disabledReason?: string
}

interface DashboardCategoryTabsProps {
  categories: DashboardCategory[]
  activeKey: string
  onSelect: (key: string) => void
  /** Labels that are visible but not available yet (e.g. planned sibling screens). */
  disabledKeys?: string[]
  /** ID of the panel the selected tab controls, when the page renders one. */
  panelId?: string
  /** Accessible name for the tab list. */
  ariaLabel?: string
}

/**
 * The dashboard category bar.
 *
 * Every label is always visible. The selected category is the orange pill; switching
 * category gives it a small pop, and the panel itself slides in from the direction of
 * travel (handled by DashboardPage).
 */
export function DashboardCategoryTabs({
  categories,
  activeKey,
  onSelect,
  disabledKeys = [],
  panelId,
  ariaLabel = 'Dashboard categories',
}: DashboardCategoryTabsProps) {
  const buttons = useRef(new Map<string, HTMLButtonElement>())
  const isDisabled = (key: string) => disabledKeys.includes(key)
  const enabled = categories.filter((category) => !isDisabled(category.key))

  const selectAndFocus = (key: string) => {
    onSelect(key)
    // The new panel and the active pill render on the next frame; keep keyboard focus
    // on the tab the user just chose.
    requestAnimationFrame(() => buttons.current.get(key)?.focus())
  }

  const move = (delta: number) => {
    if (enabled.length === 0) return
    const current = Math.max(0, enabled.findIndex((category) => category.key === activeKey))
    const next = (current + delta + enabled.length) % enabled.length
    selectAndFocus(enabled[next].key)
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className="card mx-auto flex w-fit max-w-full items-center gap-1 overflow-x-auto p-1.5 scrollbar-slim"
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault()
          move(1)
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault()
          move(-1)
        } else if (event.key === 'Home') {
          event.preventDefault()
          selectAndFocus(enabled[0]?.key ?? activeKey)
        } else if (event.key === 'End') {
          event.preventDefault()
          selectAndFocus(enabled[enabled.length - 1]?.key ?? activeKey)
        }
      }}
    >
      {categories.map((category) => {
        const selected = category.key === activeKey
        const disabled = isDisabled(category.key)
        const Icon = category.icon

        return (
          <button
            key={category.key}
            ref={(node) => {
              if (node) buttons.current.set(category.key, node)
              else buttons.current.delete(category.key)
            }}
            type="button"
            role="tab"
            id={`dashboard-tab-${category.key}`}
            aria-selected={selected}
            aria-disabled={disabled || undefined}
            aria-controls={selected && panelId ? panelId : undefined}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            title={
              disabled
                ? (category.disabledReason ?? `${category.label} — coming soon`)
                : category.label
            }
            onClick={() => {
              if (!disabled) onSelect(category.key)
            }}
            className={[
              'relative flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-xl px-3 text-xs font-semibold',
              'transition-all duration-300 motion-reduce:transition-none',
              selected
                ? 'animate-tab-pop bg-salsa-500 text-white shadow-sm shadow-salsa-500/30 motion-reduce:animate-none'
                : disabled
                  ? 'cursor-not-allowed text-slate-300 opacity-50 dark:text-slate-600'
                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white',
            ].join(' ')}
          >
            {Icon && (
              <span className="flex" aria-hidden="true">
                <Icon className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="whitespace-nowrap">{category.label}</span>
          </button>
        )
      })}
    </div>
  )
}

export default DashboardCategoryTabs
